/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @version 1.23.0
 * @since 1.19.0
 */

import { useCallback } from 'react';

import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { EBadgeStatus } from '@/main/components/common/Badge/BadgeStatus';
import { ArrayElement } from '@/main/types/globalTypes';
import { formatDate, formatPriceWithMeasureUnit } from '@/main/utils/formatText';
import { EllipseText, LinkBlock } from '@components/common';
import { ELinkTarget } from '@components/common/ExternalLink/ExternalLink';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import {
    makeSelectPageSubscriberIdTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';

import type { IProductItem } from '../types';
import { PersonalAccountBadge, ProductCardParamStyled, ProductCardStyled } from './styled/SubscriberIdProductStyled';
import { btnConnection } from './utils';

interface ISubscriberIdProductCard {
    onDisconnect: () => void;
    product: IProductItem;
}

const SubscriberIdProductCard = ({ product, onDisconnect }: ISubscriberIdProductCard) => {
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const {
        tabs: { products: tProducts },
    } = useSelector(makeSelectPageSubscriberIdTranslation());

    const formatProductPrice = (product: IProductItem) => !product.payment
        ? tProducts.priceFree
        : formatPriceWithMeasureUnit(
            product.payment.value,
            translationCommon.currency,
            product.payment.periodLength,
            product.payment.unit,
        );

    const renderPlanDate = useCallback(
        (plannedDate: ArrayElement<IProductItem['plannedDate']>) => (
            <ProductCardParamStyled key={plannedDate.date}>
                <div className="product-card__param-label">
                    {plannedDate.type === 'activation' ? tProducts.datePlanConnected : tProducts.datePlanDisconnected}
                    :&nbsp;
                </div>
                {formatDate(plannedDate.date)}
            </ProductCardParamStyled>
        ),
        [tProducts],
    );

    const buttonState = btnConnection(product);

    return (
        <ArrowTooltip
            disableHoverListener={(product.plannedDate?.length || 0) < 3}
            maxHeight="none"
            maxWidth="300px"
            message={product.plannedDate?.map(renderPlanDate)}
            placement="top"
            title={product.title}
        >
            <ProductCardStyled>
                <div className="product-card__title">{product.title}</div>
                {product.payment && (
                    <ProductCardParamStyled>
                        <div className="product-card__param-label">{tProducts.price}:&nbsp;</div>
                        {formatProductPrice(product)}
                    </ProductCardParamStyled>
                )}
                <div className="product-card__params">
                    {product.activationDate && (
                        <ProductCardParamStyled>
                            <div className="product-card__param-label">{tProducts.date}:&nbsp;</div>
                            {formatDate(product.activationDate)}
                        </ProductCardParamStyled>
                    )}
                    {
                        product.plannedDate?.map((plannedDate, index) => {
                            if (index === 2) return (
                                <ProductCardParamStyled>{tProducts.datePlanShowMore}</ProductCardParamStyled>
                            );

                            return (index === 0 || index === 1)
                                && renderPlanDate(plannedDate);
                        })
                    }
                    {
                        !!product.description &&
                        <div className="product-card__description">
                            <EllipseText lines={3} title={product.description}>
                                {product.description}
                            </EllipseText>
                        </div>
                    }
                    {
                        product.descriptionLink && (
                            <LinkBlock
                                isExternalUrl
                                target={ELinkTarget.BLANK}
                                url={product.descriptionLink}
                            >
                                {tProducts.descriptionLink}
                            </LinkBlock>
                        )
                    }
                </div>
                {product.isPersonalAccountProduct && (
                    <PersonalAccountBadge
                        dataName="subscriber-product-status"
                        status={EBadgeStatus.active}
                        text={tProducts.personalAccount}
                    />
                )}
                {buttonState?.isVisible && (
                    <div
                        className={classNames('product-card__param-link', {
                            disabled: buttonState?.isDisabled,
                        })}
                    >
                        <ArrowTooltip title={buttonState?.reason}>
                            <span>
                                <LinkBlock
                                    dataName="subscriber-product-disconnect"
                                    disabled={buttonState?.isDisabled}
                                    onClick={onDisconnect}
                                >
                                    {tProducts.btnConnection}
                                </LinkBlock>
                            </span>
                        </ArrowTooltip>
                    </div>
                )}
            </ProductCardStyled>
        </ArrowTooltip>
    );
};

export default SubscriberIdProductCard;
